import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, CardContent, Stack } from "@mui/material";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { useCreateProfessionalReference } from "../api/useCreateProfessionalReference";
import { invalidateProfessionalReferences } from "../api/useGetProfessionalReferences";
import {
  FORM_FIELDS,
  KNOWN_CREATE_REFERENCE_USER_ERROR_MESSAGES,
  PROFESSIONAL_SHARE_FLOW,
} from "../constants";
import { useShareProfessionalReference } from "../hooks/useShareProfessionalReference";
import { type ReferenceFormData } from "../types";
import { getErrorMessage } from "../utils/errors";
import { transformFormDataToRequest } from "../utils/formatters";
import { getReferenceFormFieldId } from "../utils/formHelpers";
import { referenceFormSchema } from "../utils/validation";
import { InputFormField } from "./InputFormField";
import { MonthYearPickerField } from "./MonthYearPickerField";

/**
 * Delay in milliseconds to wait after a date picker closes before triggering the next action.
 * This ensures the picker's closing animation completes and the DOM is ready for the next interaction.
 * Based on the proven delay from the old implementation for reliable mobile keyboard/picker transitions.
 */
const DATE_PICKER_TRANSITION_DELAY = 500;

export interface ReferenceFormProps {
  id: string;
  count: number;
  onSuccess?: () => void;
  initialFocusFirstField?: boolean;
}

export const ReferenceForm = forwardRef<HTMLDivElement, ReferenceFormProps>(
  ({ id: formId, count, onSuccess, initialFocusFirstField }, ref) => {
    const { showSuccessToast, showErrorToast } = useToast();
    const queryClient = useQueryClient();

    const employmentStartDateRef = useRef<HTMLInputElement>(null);
    const employmentEndDateRef = useRef<HTMLInputElement>(null);

    const {
      control,
      handleSubmit,
      reset,
      watch,
      formState: { isSubmitting, isValid },
      setFocus,
    } = useForm<ReferenceFormData>({
      mode: "onTouched",
      resolver: zodResolver(referenceFormSchema),
      defaultValues: {
        [FORM_FIELDS.EMPLOYER_NAME]: "",
        [FORM_FIELDS.WORKER_TITLE]: "",
        [FORM_FIELDS.EMPLOYMENT_START_DATE]: undefined,
        [FORM_FIELDS.EMPLOYMENT_END_DATE]: undefined,
        [FORM_FIELDS.REFERENCE_FIRST_NAME]: "",
        [FORM_FIELDS.REFERENCE_LAST_NAME]: "",
      },
      shouldFocusError: true,
    });

    useEffect(() => {
      if (!initialFocusFirstField) {
        return;
      }
      setFocus(FORM_FIELDS.EMPLOYER_NAME);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Focus on first field only when form mounts
    }, []);

    const [employmentStartDate, employmentEndDate] = watch([
      FORM_FIELDS.EMPLOYMENT_START_DATE,
      FORM_FIELDS.EMPLOYMENT_END_DATE,
    ]);

    const { mutate: shareProfessionalReference } = useShareProfessionalReference();

    const { mutateAsync: createReferenceMutation } = useCreateProfessionalReference();

    async function onSubmit(formData: ReferenceFormData) {
      try {
        await createReferenceMutation(transformFormDataToRequest(formData), {
          onSuccess: async ({ data: reference, inviteMessage }) => {
            logEvent(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCE_CREATED, {
              referenceCount: count,
            });

            showSuccessToast("Reference added successfully.");

            // Open share sheet after creation
            shareProfessionalReference(
              { inviteMessage },
              {
                onSuccess: (shareResult) => {
                  logEvent(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCE_SHARED, {
                    referenceId: reference._id,
                    flow: PROFESSIONAL_SHARE_FLOW.CREATE,
                    ...(isDefined(shareResult) && { appName: shareResult.activityType }),
                  });
                },
              }
            );
          },
          onError: (error) => {
            const defaultErrorMessage = "Failed to create reference. Please try again.";
            const errorMessage = getErrorMessage(error);

            const userErrorMessage = errorMessage
              ? KNOWN_CREATE_REFERENCE_USER_ERROR_MESSAGES[errorMessage] ?? defaultErrorMessage
              : defaultErrorMessage;

            showErrorToast(userErrorMessage);

            logError(APP_V2_APP_EVENTS.CREATE_PROFESSIONAL_REFERENCE_ERROR, {
              error,
              metadata: {
                errorMessage,
              },
            });
          },
        });
      } catch {
        return;
      }

      // Invalidate professional references to update the list
      // We wait for invalidation to keep `isSubmitting` true until the list is updated
      await invalidateProfessionalReferences(queryClient);

      // Reset form and call success callback
      reset();
      onSuccess?.();
    }

    return (
      <Card ref={ref} outlined variant="tertiary" sx={{ p: 2 }}>
        <CardContent>
          <Text variant="h5" sx={{ mb: 6 }}>
            Reference {count}
          </Text>

          <Box
            id={formId}
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 5 }}
            aria-label="Add Reference"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Employer Name */}
            <InputFormField
              formId={formId}
              name={FORM_FIELDS.EMPLOYER_NAME}
              control={control}
              label="Employer Name"
              placeholder="eg. Memorial Hospital"
              onEnterPress={() => {
                setFocus(FORM_FIELDS.WORKER_TITLE);
              }}
            />

            {/* Worker Title */}
            <InputFormField
              formId={formId}
              name={FORM_FIELDS.WORKER_TITLE}
              control={control}
              label="Your Position"
              placeholder="eg. Registered Nurse"
              onEnterPress={() => {
                // Auto-open start date picker
                employmentStartDateRef.current?.click();
              }}
            />

            {/* Date of Employment */}
            <Box component="fieldset" sx={{ border: "none", padding: 0, margin: 0 }}>
              <Box component="legend">
                <Text inline variant="body1">
                  Date of Employment
                </Text>
              </Box>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <MonthYearPickerField
                  ref={employmentStartDateRef}
                  id={getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_START_DATE)}
                  name={FORM_FIELDS.EMPLOYMENT_START_DATE}
                  control={control}
                  maxDate={employmentEndDate}
                  title="Employment Start Date"
                  onAccept={() => {
                    // Auto-open end date picker after start date is selected
                    setTimeout(() => {
                      employmentEndDateRef.current?.click();
                    }, DATE_PICKER_TRANSITION_DELAY);
                  }}
                />
                <Text variant="body1" sx={{ minWidth: "fit-content", alignSelf: "center" }}>
                  -
                </Text>
                <MonthYearPickerField
                  ref={employmentEndDateRef}
                  id={getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_END_DATE)}
                  name={FORM_FIELDS.EMPLOYMENT_END_DATE}
                  control={control}
                  minDate={employmentStartDate}
                  title="Employment End Date"
                  onAccept={() => {
                    // Focus next field after picker closes
                    setTimeout(() => {
                      setFocus(FORM_FIELDS.REFERENCE_FIRST_NAME);
                    }, DATE_PICKER_TRANSITION_DELAY);
                  }}
                />
              </Stack>
            </Box>

            {/* Reference First Name */}
            <InputFormField
              formId={formId}
              name={FORM_FIELDS.REFERENCE_FIRST_NAME}
              control={control}
              label="Reference First Name"
              placeholder="eg. John"
              onEnterPress={() => {
                setFocus(FORM_FIELDS.REFERENCE_LAST_NAME);
              }}
            />

            {/* Reference Last Name */}
            <InputFormField
              formId={formId}
              name={FORM_FIELDS.REFERENCE_LAST_NAME}
              control={control}
              label="Reference Last Name"
              placeholder="eg. Smith"
              enterKeyHint="done"
            />

            {/* Submit Button */}
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              isLoading={isSubmitting}
              disabled={!isValid}
              size="large"
            >
              Share Reference Link
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>
    );
  }
);

ReferenceForm.displayName = "ReferenceForm";
